import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  Copy, 
  Check, 
  Loader2, 
  Link as LinkIcon,
  Mail,
  Calendar,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { FILE_KEY_CONFIGS, getFileKeyDisplayName } from '@/lib/modules/file-keys';
import { FileKey } from '@/lib/modules/types';

interface CreateClientUploadLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditId: string;
  auditName: string;
  moduleFileKeys?: FileKey[];
}

export function CreateClientUploadLinkModal({
  open,
  onOpenChange,
  auditId,
  auditName,
  moduleFileKeys = []
}: CreateClientUploadLinkModalProps) {
  const { t } = useTranslation();
  
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [selectedFileKeys, setSelectedFileKeys] = useState<FileKey[]>(moduleFileKeys);
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Available file keys
  const availableFileKeys = Object.keys(FILE_KEY_CONFIGS) as FileKey[];

  const handleFileKeyToggle = (fileKey: FileKey, checked: boolean) => {
    if (checked) {
      setSelectedFileKeys(prev => [...prev, fileKey]);
    } else {
      setSelectedFileKeys(prev => prev.filter(k => k !== fileKey));
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    setGeneratedLink(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Du måste vara inloggad');
        return;
      }

      const response = await supabase.functions.invoke('client-upload-create-token', {
        body: {
          auditId,
          clientEmail: clientEmail || null,
          clientName: clientName || null,
          expiresInDays,
          requestedFileKeys: selectedFileKeys.length > 0 ? selectedFileKeys : null
        }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const { token } = response.data;
      
      // Generate the full URL - always use production domain for client links
      const PRODUCTION_DOMAIN = 'https://auditai.tech';
      const link = `${PRODUCTION_DOMAIN}/upload/${token}`;
      
      setGeneratedLink(link);
      toast.success('Uppladdningslänk skapad!');

    } catch (err) {
      console.error('Create token error:', err);
      toast.error('Kunde inte skapa länk');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedLink) return;
    
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      toast.success('Länk kopierad!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Kunde inte kopiera');
    }
  };

  const handleClose = () => {
    // Reset state
    setClientEmail('');
    setClientName('');
    setExpiresInDays(30);
    setSelectedFileKeys(moduleFileKeys);
    setGeneratedLink(null);
    setCopied(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            Skapa kunduppladdningslänk
          </DialogTitle>
          <DialogDescription>
            Skapa en säker länk som din kund kan använda för att ladda upp filer direkt till{' '}
            <span className="font-medium">{auditName}</span>.
          </DialogDescription>
        </DialogHeader>

        {!generatedLink ? (
          <>
            <div className="space-y-4 py-4">
              {/* Client info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientName">Kundens namn (valfritt)</Label>
                  <Input
                    id="clientName"
                    placeholder="Företag AB"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="clientEmail">E-post (valfritt)</Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    placeholder="kund@foretag.se"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                  />
                </div>
              </div>

              {/* Expiration */}
              <div className="space-y-2">
                <Label htmlFor="expires" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Giltighetstid (dagar)
                </Label>
                <Input
                  id="expires"
                  type="number"
                  min={1}
                  max={365}
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 30)}
                  className="w-32"
                />
              </div>

              {/* File selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Efterfrågade filer
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Välj vilka filtyper kunden ska kunna ladda upp. Lämna tomt för alla typer.
                </p>
                <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2">
                  {availableFileKeys.slice(0, 15).map((fileKey) => (
                    <div key={fileKey} className="flex items-center space-x-2">
                      <Checkbox
                        id={fileKey}
                        checked={selectedFileKeys.includes(fileKey)}
                        onCheckedChange={(checked) => 
                          handleFileKeyToggle(fileKey, checked as boolean)
                        }
                      />
                      <label
                        htmlFor={fileKey}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {getFileKeyDisplayName(fileKey)}
                      </label>
                    </div>
                  ))}
                </div>
                {selectedFileKeys.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedFileKeys.map(key => (
                      <Badge key={key} variant="secondary" className="text-xs">
                        {getFileKeyDisplayName(key)}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Avbryt
              </Button>
              <Button onClick={handleCreate} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Skapar...
                  </>
                ) : (
                  'Skapa länk'
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
          <div className="py-4 space-y-4">
              <Alert className="bg-amber-50 dark:bg-amber-950/20 border-amber-300">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 dark:text-amber-200">
                  <span className="font-semibold">Viktigt:</span> Denna länk visas endast en gång. 
                  Kopiera den nu – den kan inte visas igen av säkerhetsskäl.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label>Uppladdningslänk</Label>
                <div className="flex gap-2">
                  <Input
                    value={generatedLink}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="text-xs text-muted-foreground space-y-1">
                <p>• Länken går ut om {expiresInDays} dagar</p>
                <p>• Kunden kan bara ladda upp filer, inte se eller ladda ner befintliga</p>
                <p>• Du kan återkalla länken när som helst</p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Stäng
              </Button>
              <Button onClick={handleCopy}>
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Kopierad!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Kopiera länk
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
